import { useState, useEffect } from "react";
import "./addProduct.css";
import DynamicGemstoneCells from "./DynamicGemstoneCells";
import useCreate from "../../hooks/useCreate";
import useGet from "../../hooks/useGet";
import { triggerPriceUpdateOne } from "utils/priceUpdateAll";

const DEFAULT_GOLD_PURITY = "14K";
const PRECIOUS_TYPES = ["Ruby", "Sapphire", "Emerald", "Navratan", "Pearl"];
const SEMI_PRECIOUS_TYPES = [
  "Opal",
  "Garnet",
  "Topaz",
  "Amethyst",
  "Citrine",
  "Peridot",
  "Aquamarine",
  "Tourmaline",
  "Tanzanite",
  "Spinel",
  "Morganite",
  "Alexandrite",
  "Coral",
  "Moonstone",
  "Turquoise",
  "Jade",
  "Onyx",
  "LapisLazuli",
  "TigerEye",
  "Kunzite"
];
const toGemKey = (type) => type.toLowerCase().replace(/\s+/g, "");

const AddProduct = () => {
  const { create: createProduct } = useCreate("/api/products");
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedTagOption, setSelectedTagOption] = useState("");
  /* ================= BASIC FORM ================= */
  const handleCheckbox = (e) => {
  const { name, checked } = e.target;
  setForm((prev) => ({
    ...prev,
    [name]: checked,
  }));
};

  const [form, setForm] = useState({
    sku: "",
    title: "",
    description: "",
    metaTitle: "",
    metaDescription: "",
    metaKeywords: "",
    isFeatured: false,
    publishStatus: false,
    goldColor: "Yellow",
    category: "",
    subcategories: [],
    tags: [],
    platinumWeight:"",
    stock: "",
  });

  /* ================= MEDIA ================= */
  const [heroImage, setHeroImage] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [video, setVideo] = useState(null);

  const [heroPreview, setHeroPreview] = useState(null);
  const [galleryPreviews, setGalleryPreviews] = useState([]);
  const [videoPreview, setVideoPreview] = useState(null);

  /* ================= GOLD / PLATINUM ================= */
  const [baseGoldWeight, setBaseGoldWeight] = useState("");
  const [finalGoldWeight, setFinalGoldWeight] = useState(0);

 

  /* ================= GEMSTONES ================= */
  const [cells, setCells] = useState({});
  const [family, setFamily] = useState("");
  

  /* ================= API FETCH ================= */
  const { data: categoriesData, loading: categoriesLoading } = useGet("/api/categories?all=true");
  const { data: tagsData, loading: tagsLoading } = useGet("/api/tag?category=manual");
  const { data: subcategoriesData, loading: subcategoriesLoading } = useGet(
    form.category ? `/api/categories/subcategories/${form.category}?all=true` : null
  );

  useEffect(() => {
    if (categoriesData?.categories && Array.isArray(categoriesData.categories)) {
      // Get only parent categories (where parentCategory is null)
      const parentCategories = categoriesData.categories.filter(cat => !cat.parentCategory && !cat.parentId);
      setCategories(parentCategories || []);
    }
  }, [categoriesData]);

  useEffect(() => {
    if (subcategoriesData?.subCategories && Array.isArray(subcategoriesData.subCategories)) {
      setSubcategories(subcategoriesData.subCategories || []);
    } else {
      setSubcategories([]);
    }
  }, [subcategoriesData]);

  useEffect(() => {
    if (!tagsData) {
      setTags([]);
      return;
    }
    if (Array.isArray(tagsData)) {
      setTags(tagsData);
      return;
    }
    if (Array.isArray(tagsData.tags)) {
      setTags(tagsData.tags);
      return;
    }
    if (Array.isArray(tagsData.data)) {
      setTags(tagsData.data);
      return;
    }
    if (Array.isArray(tagsData.items)) {
      setTags(tagsData.items);
      return;
    }
    setTags([]);
  }, [tagsData]);

  /* ================= GOLD CALC ================= */
  useEffect(() => {
    if (!baseGoldWeight) {
      setFinalGoldWeight(0);
      return;
    }
    setFinalGoldWeight(Number(Number(baseGoldWeight).toFixed(2)));
  }, [baseGoldWeight]);

  /* ================= HANDLERS ================= */
  const handleInput = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const toggleSubcategory = (sub) => {
    setForm((prev) => ({
      ...prev,
      subcategories: prev.subcategories.includes(sub)
        ? prev.subcategories.filter((s) => s !== sub)
        : [...prev.subcategories, sub],
    }));
  };

  const addSelectedTag = () => {
    if (!selectedTagOption) return;
    setForm((prev) => ({
      ...prev,
      tags: prev.tags.includes(selectedTagOption) ? prev.tags : [...prev.tags, selectedTagOption],
    }));
  };

  const removeTag = (tagId) => {
    setForm((prev) => ({
      ...prev,
      tags: prev.tags.filter((id) => id !== tagId),
    }));
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.sku) {
      alert("SKU is required");
      return;
    }

    /* ---- Build gemstones object ---- */
    const diamond = [];
    const gemstones = {
      preciousGemstones: PRECIOUS_TYPES.reduce((acc, type) => {
        acc[toGemKey(type)] = [];
        return acc;
      }, {}),
      semiPreciousGemstones: SEMI_PRECIOUS_TYPES.reduce((acc, type) => {
        acc[toGemKey(type)] = [];
        return acc;
      }, {})
    };

    Object.keys(cells).forEach((type) => {
      const isDiamond = type === "Diamond";
      const isPrecious = PRECIOUS_TYPES.includes(type);
      const isSemiPrecious = SEMI_PRECIOUS_TYPES.includes(type);
      const typeKey = toGemKey(type);
      cells[type].forEach((item) => {
        const gem = {
          type: item.Type,
          weight: item.Weight ? item.Weight.split(",").map(s => s.trim()) : [],
          size: item.Size ? item.Size.split(",").map(s => s.trim()) : [],
          shape: item.Shape ? item.Shape.split(",").map(s => s.trim()) : [],
          pieces: item.Pieces ? item.Pieces.split(",").map(s => s.trim()) : []
        };
        if (type === "Diamond" && item.Quality) {
          gem.quality = [item.Quality];
        }
        if (isDiamond) {
          diamond.push(gem);
        } else if (isPrecious) {
          gemstones.preciousGemstones[typeKey].push(gem);
        } else if (isSemiPrecious) {
          gemstones.semiPreciousGemstones[typeKey].push(gem);
        }
      });
    });

    /* ---- BUILD PAYLOAD ACCORDING TO SCHEMA ---- */
    const payload = new FormData();

    /* Basic Fields */
    payload.append("SKU", form.sku);
    payload.append("title", form.title);
    payload.append("description", form.description);
    payload.append("metaTitle", form.metaTitle);
    payload.append("metaDescription", form.metaDescription);
    payload.append(
      "metaKeywords",
      JSON.stringify(form.metaKeywords.split(",").map((keyword) => keyword.trim()).filter(Boolean))
    );
    payload.append("isFeatured", form.isFeatured);
    payload.append("isPublished", form.publishStatus);
    payload.append("category", form.category);
    const normalizedSubcategories = Array.isArray(form.subcategories)
      ? form.subcategories.filter(Boolean)
      : [];
    payload.append("subCategories", JSON.stringify(normalizedSubcategories));
    // payload.append("subcategories", JSON.stringify(normalizedSubcategories));
    normalizedSubcategories.forEach((id) => {
      // payload.append("subCategories[]", id);
      // payload.append("subcategories[]", id);
    });
    payload.append("tag", JSON.stringify(form.tags));
    payload.append("stock", String(Number(form.stock)));

    /* Media */
    if (heroImage) payload.append("heroImage", heroImage);
    galleryImages.forEach((img, idx) => {
      payload.append(`galleryImages`, img);
    });
    if (video) payload.append("productVideo", video);

    /* Metal (Gold/Platinum) - allow both together */
    const hasGold = Boolean(baseGoldWeight);
    const hasPlatinum = Boolean(form.platinumWeight);

    const metalTypes = [];
    if (hasGold) metalTypes.push("Gold");
    if (hasPlatinum) metalTypes.push("Platinum");
    metalTypes.forEach((metalType) => {
      payload.append("metal[type][]", metalType);
    });

    if (hasGold) {
      payload.append(
        "metal[gold][purity]",
        JSON.stringify([DEFAULT_GOLD_PURITY.toLowerCase()])
      );
      payload.append(
        "metal[gold][baseGoldWeight]",
        String(Number(baseGoldWeight))
      );
      payload.append(
        "metal[gold][calculatedGoldWeight]",
        String(Number(finalGoldWeight))
      );
    }

    if (hasPlatinum) {
      payload.append(
        "metal[platinum][weight]",
        String(Number(form.platinumWeight))
      );
    }


    /* Gemstones */
    payload.append("diamond", JSON.stringify(diamond));
    payload.append("gemstones", JSON.stringify(gemstones));

    try {
      const createdProduct = await createProduct(payload);
      let priceRefreshFailed = false;
      const productId =
        createdProduct?._id ||
        createdProduct?.product?._id ||
        createdProduct?.data?._id;
      try {
        await triggerPriceUpdateOne(productId);
      } catch (priceErr) {
        // Don't block the product creation on price refresh, but do surface it.
        // eslint-disable-next-line no-console
        console.warn("Price update-one failed after product create:", priceErr);
        priceRefreshFailed = true;
      }
      alert(
        priceRefreshFailed
          ? "Product added successfully, but price refresh failed. Please try again from Main Price."
          : "Product added successfully"
      );
    } catch (err) {
      console.error("ADD PRODUCT ERROR:", err.response?.data || err);
      alert(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Error adding product"
      );
    }
  };

  /* ================= UI ================= */
  return (
    <form className="product-form" onSubmit={handleSubmit}>
      <h2 className="section-title">Add Product</h2>

      <FormGroup label="SKU">
        <input name="sku" onChange={handleInput} required />
      </FormGroup>

      <FormGroup label="Title">
        <input name="title" onChange={handleInput} required />
      </FormGroup>

      <FormGroup label="Stock">
        <input name="stock" type="number" onChange={handleInput} required />
      </FormGroup>

      {/* FEATURE & PUBLISH */}
<FormGroup label="Flags">
  <label className="checkbox-inline">
    <input
      type="checkbox"
      name="isFeatured"
      checked={form.isFeatured}
      onChange={handleCheckbox}
    />
    Is Featured
  </label>

  <label className="checkbox-inline">
    <input
      type="checkbox"
      name="publishStatus"
      checked={form.publishStatus}
      onChange={handleCheckbox}
    />
    Is Published
  </label>
</FormGroup>


      <FormGroup label="Description">
        <textarea 
          name="description" 
          onChange={handleInput}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
            }
          }}
          rows={3}
          style={{ resize: 'vertical' }}
        />
      </FormGroup>

      <FormGroup label="Meta Title">
        <input
          name="metaTitle"
          value={form.metaTitle}
          onChange={handleInput}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
            }
          }}
        />
      </FormGroup>

      <FormGroup label="Meta Description">
        <textarea 
          name="metaDescription" 
          value={form.metaDescription} 
          onChange={handleInput}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
            }
          }}
          rows={3}
          style={{ resize: 'vertical' }}
        />
      </FormGroup>

      <FormGroup label="Meta Keywords (comma-separated)">
        <textarea
          name="metaKeywords"
          value={form.metaKeywords}
          onChange={handleInput}
          placeholder="ruby ring, gold gemstone jewelry"
          rows={2}
          style={{ resize: 'vertical' }}
        />
      </FormGroup>

      {/* HERO IMAGE */}
      <FormGroup label="Hero Image">
        <input
          type="file"
          accept="image/*"
          onChange={(e) => {
            const file = e.target.files[0];
            setHeroImage(file);
            setHeroPreview(URL.createObjectURL(file));
          }}
          style={{ padding: '8px', cursor: 'pointer' }}
        />
        {heroPreview && (
          <img src={heroPreview} className="preview-image" alt="Hero" style={{ marginTop: '10px' }} />
        )}
      </FormGroup>

      {/* GALLERY */}
      <FormGroup label="Gallery Images">
        <input
          type="file"
          multiple
          accept="image/*"
          onChange={(e) => {
            const files = [...e.target.files];
            setGalleryImages(files);
            setGalleryPreviews(files.map((f) => URL.createObjectURL(f)));
          }}
          style={{ padding: '8px', cursor: 'pointer' }}
        />
        <div className="preview-grid">
          {galleryPreviews.map((src, i) => (
            <img key={i} src={src} className="preview-sm" alt="" style={{height:"100px", width:"100px"}} />
          ))}
        </div>
      </FormGroup>

      {/* VIDEO */}
      <FormGroup label="Product Video">
        <input
          type="file"
          accept="video/*"
          onChange={(e) => {
            const file = e.target.files[0];
            setVideo(file);
            setVideoPreview(URL.createObjectURL(file));
          }}
          style={{ padding: '8px', cursor: 'pointer' }}
        />
        {videoPreview && (
          <div style={{ position: 'relative', display: 'inline-block', marginTop: '10px' }}>
            <video src={videoPreview} controls width="240" style={{ borderRadius: '6px', border: '1px solid #ddd', display: 'block' }} />
            <button
              type="button"
              onClick={() => {
                setVideo(null);
                setVideoPreview(null);
              }}
              style={{
                position: 'absolute',
                top: '6px',
                right: '6px',
                width: '24px',
                height: '24px',
                backgroundColor: 'rgba(0, 0, 0, 0.65)',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0'
              }}
            >
              ✕
            </button>
          </div>
        )}
      </FormGroup>

      {/* GOLD */}
      <FormGroup label="Base Gold Weight (14K default purity)">
        <input
          type="number"
          value={baseGoldWeight}
          onChange={(e) => setBaseGoldWeight(e.target.value)}
        />
      </FormGroup>

      <FormGroup label="Calculated Gold Weight">
        <input type="number" value={finalGoldWeight} readOnly />
      </FormGroup>

      <FormGroup label="Platinum Weight">
  <input
    type="number"
    name="platinumWeight"
    value={form.platinumWeight}
    onChange={handleInput}
  />
</FormGroup>


      {/* CATEGORY */}
      <FormGroup label="Category">
        <select
          value={form.category}
          onChange={(e) =>
            setForm({ ...form, category: e.target.value, subcategories: [] })
          }
        >
          <option value="">Select</option>
         {categories
  .filter(cat => cat.name?.toLowerCase() !== "home")
  .map((cat) => (
    <option key={cat._id} value={cat._id}>
      {cat.name}
    </option>
  ))}

        </select>
      </FormGroup>

      {form.category && (
        <div className="subcategory-box">
          {subcategories.map((sub) => (
            <label key={sub._id}>
              <input
                type="checkbox"
                checked={form.subcategories.includes(sub._id)}
                onChange={() => toggleSubcategory(sub._id)}
              />
              {sub.name}
            </label>
          ))}
        </div>
      )}

      <FormGroup label="Tags">
        {tagsLoading ? (
          <p style={{ margin: 0 }}>Loading tags...</p>
        ) : tags.length === 0 ? (
          <p style={{ margin: 0 }}>No tags found</p>
        ) : (
          <>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <select
                value={selectedTagOption}
                onChange={(e) => setSelectedTagOption(e.target.value)}
                style={{ width: "100%", padding: "10px" }}
              >
                <option value="">Select Tag</option>
                {tags.map((tag) => {
                  const id = tag._id || tag.id;
                  return (
                    <option key={id} value={id}>
                      {tag.name}
                    </option>
                  );
                })}
              </select>
              <button
                type="button"
                onClick={addSelectedTag}
                style={{
                  padding: "10px 14px",
                  backgroundColor: "#1976d2",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  fontWeight: 600,
                  cursor: "pointer",
                  minWidth: "72px"
                }}
              >
                Add
              </button>
            </div>

            {form.tags.length > 0 && (
              <div className="subcategory-box">
                {form.tags.map((tagId) => {
                  const selectedTag = tags.find((tag) => (tag._id || tag.id) === tagId);
                  return (
                    <label key={tagId} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <span>{selectedTag?.name || tagId}</span>
                      <button
                        type="button"
                        onClick={() => removeTag(tagId)}
                        style={{
                          width: "18px",
                          height: "18px",
                          backgroundColor: "#e53935",
                          color: "#fff",
                          border: "none",
                          borderRadius: "50%",
                          fontSize: "11px",
                          lineHeight: 1,
                          fontWeight: 600,
                          cursor: "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center"
                        }}
                      >
                        x
                      </button>
                    </label>
                  );
                })}
              </div>
            )}
          </>
        )}
      </FormGroup>

      {/* GEMSTONES */}
      <DynamicGemstoneCells
        cells={cells}
        setCells={setCells}
        family={family}
        setFamily={setFamily}
      />

      <button type="submit">Add Product</button>
    </form>
  );
};

const FormGroup = ({ label, children }) => (
  <div className="form-group">
    <label>{label}</label>
    {children}
  </div>
);

export default AddProduct;
